import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class QueueImageLoaderService {
  private readonly queue: Array<() => void> = [];
  private readonly inFlight = new Map<string, HTMLImageElement>();
  private readonly inFlightPromise = new Map<string, Promise<string>>();
  private readonly cache = new Set<string>();

  private loading = 0;
  private readonly maxConcurrent = 2;
  private blocked = false;

  async prioritizeNetwork<T>(fn: () => Promise<T>): Promise<T> {
    const prevBlocked = this.blocked;
    this.blocked = true;

    const snapshot = this.queue.splice(0);

    try {
      return await fn();
    } finally {
      if (snapshot.length) {
        this.queue.unshift(...snapshot);
      }

      this.blocked = prevBlocked;
      this.processQueue();
    }
  }

  loadImage(src: string): Promise<string> {
    if (!src) return Promise.reject(new Error('Empty src'));
    if (this.cache.has(src)) return Promise.resolve(src);

    const existing = this.inFlightPromise.get(src);
    if (existing) return existing;

    const p = new Promise<string>((resolve, reject) => {
      const load = () => {

        if (this.blocked) {
          this.queue.push(load);
          return;
        }

        this.loading++;
        const img = new Image();
        this.inFlight.set(src, img);

        const cleanup = (ok: boolean) => {
          this.loading--;
          this.inFlight.delete(src);
          this.inFlightPromise.delete(src);
          this.processQueue();
          if (ok) {
            this.cache.add(src);
            resolve(src);
          } else {
            reject(new Error('Image load failed'));
          }

          this.processQueue();
        };

        img.onload = () => cleanup(true);
        img.onerror = () => cleanup(false);
        img.src = src;
      };

      if (!this.blocked && this.loading < this.maxConcurrent) {
        load();
      } else {
        this.queue.push(load);
      }
    });

    this.inFlightPromise.set(src, p);
    return p;
  }

  private processQueue() {
    if (this.blocked) return;
    while (this.queue.length > 0 && this.loading < this.maxConcurrent) {
      const next = this.queue.shift();
      next();
    }
  }
}
